import { useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Search, UserPlus, Download, ExternalLink, Building2, Plus, Pencil, FileText, Send, Users, ChevronLeft, ChevronRight, Upload, MoreHorizontal, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { formatINRShort, formatDate } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { UniverseCleaningTools } from "@/components/UniverseCleaningTools";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SalespersonFilter } from "@/components/SalespersonFilter";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { UniverseCompanyDialog } from "@/components/UniverseCompanyDialog";
import { UniverseAccountsDialog } from "@/components/UniverseAccountsDialog";
import { CallHistoryList } from "@/components/CallHistoryCard";

const capFirst = (s = "") => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const ALL = "all";
const CLASS_STYLE = {
  "A+": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "A": "bg-blue-50 text-blue-700 border-blue-200",
  "B+": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "B": "bg-slate-100 text-slate-600 border-slate-200",
  "C": "bg-slate-50 text-slate-500 border-slate-200",
};

function Chip({ label, value, tone = "slate", testid }) {
  const tones = { slate: "bg-slate-100 text-slate-700", emerald: "bg-emerald-50 text-emerald-700", brand: "bg-[#FFEAE7] text-brand" };
  return (
    <div className={`rounded-lg px-4 py-2 ${tones[tone]}`} data-testid={testid}>
      <span className="font-heading text-lg font-bold">{value}</span>
      <span className="text-xs ml-1.5">{label}</span>
    </div>
  );
}

function RelatedDialog({ row, onOpenChange }) {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["uni-related", row?.id],
    queryFn: async () => (await api.get(`/master-data/${row.id}/related`)).data,
    enabled: !!row?.id,
  });
  const rows = [
    ...(data?.opportunities || []).map((r) => ({ ...r, kind: "Opportunity", to: `/deals/${r.id}` })),
    ...(data?.leads || []).map((r) => ({ ...r, kind: "Lead", to: `/leads/${r.id}` })),
  ];
  return (
    <Dialog open={!!row} onOpenChange={(v) => !v && onOpenChange(null)}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="universe-related-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Building2 className="h-4 w-4 text-slate-400" /> {row?.company}{row?.location ? ` — ${row.location}` : ""}
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">All leads & opportunities we are currently working for this company. Click a row to open it. All figures in INR.</p>
        {isLoading ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center" data-testid="universe-related-empty">No leads or opportunities linked to this company yet.</p>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded-md">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                {["", "Project Name", "UniVicoustic Specified", "Value", "EDC", "Stage / Status", "Owner"].map((h) => (
                  <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</TableHead>
                ))}
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={`${r.kind}-${r.id}`} onClick={() => navigate(r.to)} className="cursor-pointer hover:bg-slate-50" data-testid={`universe-related-row-${r.id}`}>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${r.kind === "Opportunity" ? "bg-indigo-50 text-indigo-700" : "bg-slate-100 text-slate-600"}`}>
                        {r.kind === "Opportunity" ? <Send className="h-3 w-3" /> : <FileText className="h-3 w-3" />} {r.kind}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium text-slate-800">{r.project_name || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600">{r.univic_specified || "—"}</TableCell>
                    <TableCell className="font-mono text-sm whitespace-nowrap">{r.value ? formatINRShort(r.value) : "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600 whitespace-nowrap">{r.edc ? formatDate(r.edc) : "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600 whitespace-nowrap">{r.status || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-500 whitespace-nowrap">{r.owner_name || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {row && (
          <div className="mt-2">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2" data-testid="universe-call-history-heading">Call History</p>
            <CallHistoryList linkedType="universe" linkedId={row.id} company={row.company} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Universe({ dealerView }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [q, setQ] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const [loc, setLoc] = useState(ALL);
  const [cat, setCat] = useState(ALL);
  const [seg, setSeg] = useState(ALL);
  const [sector, setSector] = useState(ALL);
  const [addedBy, setAddedBy] = useState(ALL);
  const [mgr, setMgr] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [busy, setBusy] = useState("");
  const [dialog, setDialog] = useState(null);
  const [related, setRelated] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(1);

  const canImport = !["management", "factory", "marketing"].includes(user?.role);
  const excelRef = useRef(null);
  const [uploadingExcel, setUploadingExcel] = useState(false);

  const uploadUniverseExcel = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploadingExcel(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/master-data/upload-universe-excel", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 300000,
      });
      const s = r.data;
      toast.success(`Universe updated — ${s.new_companies} new companies, ${s.new_contacts} new contacts (${s.skipped_duplicate_contacts} duplicates skipped). Totals: ${s.total_universe} companies / ${s.total_contacts} contacts.`, { duration: 9000 });
      qc.invalidateQueries({ queryKey: ["universe"] });
      qc.invalidateQueries({ queryKey: ["master-data"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingExcel(false);
    }
  };
  const canAssign = ["super_admin", "sales_head", "region_head", "sales_manager"].includes(user?.role);
  const canDeleteUni = ["super_admin", "sales_head"].includes(user?.role);

  const { data, isLoading } = useQuery({
    queryKey: ["universe", q, loc, cat, seg, sector, addedBy, mgr, status, page, !!dealerView],
    queryFn: async () => (await api.get("/master-data", { params: {
      q, location: loc === ALL ? "" : loc, category: cat === ALL ? "" : cat, segment: seg === ALL ? "" : seg,
      sector: sector === ALL ? "" : sector,
      added_by: addedBy === ALL ? "" : addedBy, manager: mgr === ALL ? "" : mgr, status: status === ALL ? "" : status,
      page, limit: 100, dealer_view: dealerView ? 1 : 0 } })).data,
    keepPreviousData: true,
  });
  const { data: assignees = [] } = useQuery({
    queryKey: ["md-assignees"],
    queryFn: async () => (await api.get("/master-data/assignees")).data,
    enabled: canAssign,
  });

  const refresh = () => { qc.invalidateQueries({ queryKey: ["universe"] }); qc.invalidateQueries({ queryKey: ["leads"] }); };

  const importRow = async (row) => {
    setBusy(row.id);
    try {
      const { data: res } = await api.post(`/master-data/${row.id}/import`);
      toast.success(`${row.company} imported as your lead.`, {
        action: { label: "Open lead", onClick: () => navigate(`/leads/${res.lead_id}`) },
      });
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const assignRow = async (row, userId) => {
    setBusy(row.id);
    try {
      const { data: res } = await api.post(`/master-data/${row.id}/assign`, { user_id: userId });
      toast.success(`${row.company} assigned to ${res.owner_name} — lead created & they've been notified.`);
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const delRow = async (row) => {
    try {
      await api.delete(`/master-data/${row.id}`);
      toast.success(`${row.company} removed from the Universe`);
      refresh();
    } catch (e) { toast.error(apiError(e)); throw e; }
  };

  const items = data?.items || [];
  const meta = data?.meta || {};
  const locations = useMemo(() => (meta.locations || []).filter(Boolean), [meta.locations]);
  const categories = useMemo(() => (meta.categories || []).filter(Boolean), [meta.categories]);
  const segments = useMemo(() => (meta.segments || []).filter(Boolean), [meta.segments]);
  const sectors = useMemo(() => (meta.sectors || []).filter(Boolean), [meta.sectors]);
  const filtered = q.trim() || loc !== ALL || cat !== ALL || seg !== ALL || addedBy !== ALL || mgr !== ALL || status !== ALL;

  return (
    <div>
      <PageHeader title={dealerView ? "Dealer Universe" : "Universe"}
        subtitle={dealerView ? "Companies added by dealer partners — sales need to allocate these." : "Company-level account intelligence — target companies with type, class, segment & sales manager. Pick as leads or assign to your team."}>
        {!dealerView && !["dealer", "factory", "marketing"].includes(user?.role) && (
          <>
            <Button variant="outline" className="gap-1.5" data-testid="universe-template-btn"
              onClick={async () => {
                try {
                  const r = await api.get("/master-data/import-template", { responseType: "blob" });
                  const url = URL.createObjectURL(r.data);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "universe_import_template.xlsx";
                  a.click();
                  setTimeout(() => URL.revokeObjectURL(url), 4000);
                } catch { toast.error("Template download failed"); }
              }}>
              <FileText className="h-4 w-4" /> Template
            </Button>
            <Button variant="outline" className="gap-1.5" data-testid="export-universe-excel-btn"
              onClick={async () => {
                try {
                  toast.info("Preparing Universe export…");
                  const r = await api.get("/master-data/export-excel", { responseType: "blob", timeout: 120000 });
                  const url = URL.createObjectURL(r.data);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `universe_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
                  a.click();
                  URL.revokeObjectURL(url);
                } catch { toast.error("Export failed"); }
              }}>
              <Download className="h-4 w-4" /> Export Excel
            </Button>
            <input ref={excelRef} type="file" accept=".xlsx" className="hidden" onChange={uploadUniverseExcel} data-testid="universe-excel-input" />
            <Button variant="outline" className="gap-1.5" onClick={() => excelRef.current?.click()} disabled={uploadingExcel} data-testid="import-universe-excel-btn">
              {uploadingExcel ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Import Excel
            </Button>
          </>
        )}
        {canImport && (
          <Button onClick={() => setDialog({})} className="bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="add-universe-btn">
            <Plus className="h-4 w-4" /> Add Company
          </Button>
        )}
      </PageHeader>

      {!dealerView && ["super_admin", "management", "sales_head"].includes(user?.role) && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <UniverseCleaningTools onChanged={refresh} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Chip label="companies" value={meta.total ?? "—"} testid="uni-chip-total" />
        <Chip label="available" value={meta.available ?? "—"} tone="brand" testid="uni-chip-available" />
        <Chip label="in CRM" value={meta.in_crm ?? "—"} tone="emerald" testid="uni-chip-incrm" />
        {filtered && <Chip label="matching" value={meta.matched ?? "—"} tone="brand" testid="uni-chip-matched" />}
        {addedBy !== ALL && <Chip label={`accounts added by ${addedBy === "__import" ? "Import" : (meta.adders || []).find((a) => a.id === addedBy)?.name || "…"}`} value={meta.matched ?? "—"} tone="brand" testid="uni-chip-added-by-count" />}
        <div className="relative flex-1 min-w-[180px] max-w-xs ml-auto">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search company…" className="h-9 pl-9" data-testid="uni-search" />
        </div>
        <Select value={sector} onValueChange={(v) => { setSector(v); setPage(1); }}>
          <SelectTrigger className="h-9 w-[150px]" data-testid="uni-filter-sector"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All Sectors</SelectItem>
            {sectors.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            <SelectItem value="__none">Unassigned</SelectItem>
          </SelectContent>
        </Select>
        <Select value={seg} onValueChange={(v) => { setSeg(v); setPage(1); }}>
          <SelectTrigger className="h-9 w-[150px]" data-testid="uni-filter-segment"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All Segments</SelectItem>
            {segments.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            <SelectItem value="__none">Unassigned</SelectItem>
          </SelectContent>
        </Select>
        <Select value={loc} onValueChange={(v) => { setLoc(v); setPage(1); }}>
          <SelectTrigger className="h-9 w-[140px]" data-testid="uni-filter-location"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All HQs</SelectItem>
            {locations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <SalespersonFilter value={mgr} onChange={(v) => { setMgr(v); setPage(1); }} testid="uni-filter-manager" width="w-[160px]" />
        <Select value={addedBy} onValueChange={(v) => { setAddedBy(v); setPage(1); }}>
          <SelectTrigger className="h-9 w-[190px]" data-testid="uni-filter-added-by"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Added by — all</SelectItem>
            {(meta.adders || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.count})</SelectItem>)}
            {meta.import_count > 0 && <SelectItem value="__import">Import ({meta.import_count})</SelectItem>}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="h-9 w-[130px]" data-testid="uni-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All Statuses</SelectItem>
            <SelectItem value="available">Available</SelectItem>
            <SelectItem value="in_crm">In CRM</SelectItem>
          </SelectContent>
        </Select>
        <Select value={cat} onValueChange={(v) => { setCat(v); setPage(1); }}>
          <SelectTrigger className="h-9 w-[130px]" data-testid="uni-filter-class"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All Classes</SelectItem>
            {categories.map((c) => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          {isLoading ? <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                {["Company", "Accounts", "HQ", "Type", "Class", "Segment", "Contact", "Sales Manager", "Added By", "Status", ""].map((h) => (
                  <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>
                ))}
              </TableRow></TableHeader>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.id} data-testid={`uni-row-${row.id}`}>
                    <TableCell className="font-semibold text-slate-900 max-w-[170px]">
                      <button onClick={() => setRelated(row)} className="flex items-center gap-2 hover:text-brand transition-colors text-left w-full" title={`${row.company} — view linked leads & opportunities`} data-testid={`uni-company-${row.id}`}>
                        <Building2 className="h-3.5 w-3.5 text-slate-300 shrink-0" /><span className="truncate">{capFirst(row.company)}</span>
                      </button>
                    </TableCell>
                    <TableCell>
                      <button onClick={() => setAccounts(row)}
                        className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded transition-colors ${row.accounts ? "text-indigo-700 bg-indigo-50 hover:bg-indigo-100" : "text-slate-400 bg-slate-50 hover:bg-slate-100"}`}
                        title="View this company's contacts (accounts)" data-testid={`uni-accounts-${row.id}`}>
                        <Users className="h-3 w-3" /> {row.accounts || 0}
                      </button>
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm max-w-[110px] truncate" title={row.location || ""}>{row.location || "—"}</TableCell>
                    <TableCell className="text-slate-600 text-sm">{row.type || "—"}</TableCell>
                    <TableCell>{row.category ? <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${CLASS_STYLE[row.category] || CLASS_STYLE.B}`}>{row.category}</span> : <span className="text-slate-300">—</span>}</TableCell>
                    <TableCell className="text-slate-600 text-sm">
                      {row.segment || "—"}
                      {row.sector && <span className="block text-xs text-slate-400">{row.sector}</span>}
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm min-w-[220px]">
                      {row.contact_person || row.contact_number ? (
                        <span>{row.contact_person}{row.contact_number ? ` · ${row.contact_number}` : ""}</span>
                      ) : <span className="text-slate-300">—</span>}
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm">{row.sales_manager_name || "—"}</TableCell>
                    <TableCell className="text-slate-500 text-xs whitespace-nowrap max-w-[110px] truncate" title={row.added_by || ""} data-testid={`uni-added-by-${row.id}`}>{row.added_by || "—"}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {row.status === "in_crm" ? (
                        <button onClick={() => setRelated(row)}
                                className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded hover:bg-emerald-100"
                                title={`View all linked leads & opportunities${row.owner_name ? ` · Owner: ${row.owner_name}` : ""}`}
                                data-testid={`uni-status-${row.id}`}>
                          In CRM <ExternalLink className="h-3 w-3" />
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400" data-testid={`uni-status-${row.id}`}>Available</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right w-12">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-slate-700" disabled={busy === row.id} data-testid={`uni-actions-${row.id}`}>
                            {busy === row.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          {canImport && (user?.role !== "dealer" || row.created_by_dealer === user?.id) && (
                            <DropdownMenuItem onClick={() => setDialog(row)} className="gap-2" data-testid={`uni-edit-${row.id}`}>
                              <Pencil className="h-3.5 w-3.5 text-slate-400" /> Edit company
                            </DropdownMenuItem>
                          )}
                          {row.status !== "in_crm" && canImport && (
                            <DropdownMenuItem onClick={() => importRow(row)} className="gap-2" data-testid={`uni-import-${row.id}`}>
                              <Download className="h-3.5 w-3.5 text-slate-400" /> Import as my lead
                            </DropdownMenuItem>
                          )}
                          {row.status !== "in_crm" && canAssign && (
                            <DropdownMenuSub>
                              <DropdownMenuSubTrigger className="gap-2" data-testid={`uni-assign-${row.id}`}>
                                <UserPlus className="h-3.5 w-3.5 text-slate-400" /> Assign to…
                              </DropdownMenuSubTrigger>
                              <DropdownMenuSubContent className="max-h-64 overflow-y-auto">
                                {assignees.map((a) => (
                                  <DropdownMenuItem key={a.id} onClick={() => assignRow(row, a.id)} data-testid={`uni-assign-${row.id}-${a.id}`}>{a.name}</DropdownMenuItem>
                                ))}
                              </DropdownMenuSubContent>
                            </DropdownMenuSub>
                          )}
                          {row.status !== "in_crm" && (canDeleteUni || (user?.role === "dealer" && row.created_by_dealer === user?.id)) && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => setConfirmDel(row)} className="gap-2 text-red-600 focus:text-red-700" data-testid={`uni-delete-${row.id}`}>
                                <Trash2 className="h-3.5 w-3.5" /> Remove from Universe
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-10 text-slate-400">No companies match your filters.</TableCell></TableRow>}
              </TableBody>
            </Table>
          )}
        </div>
        {meta.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-100" data-testid="uni-pagination">
            <p className="text-xs text-slate-400">
              Showing {(meta.page - 1) * meta.limit + 1}–{Math.min(meta.page * meta.limit, meta.matched)} of {meta.matched} companies
            </p>
            <div className="flex items-center gap-1.5">
              <Button variant="outline" size="sm" className="h-8 px-2.5" disabled={meta.page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="uni-prev-page"><ChevronLeft className="h-4 w-4" /></Button>
              <span className="text-xs text-slate-500 px-1.5">{meta.page} / {meta.pages}</span>
              <Button variant="outline" size="sm" className="h-8 px-2.5" disabled={meta.page >= meta.pages} onClick={() => setPage((p) => p + 1)} data-testid="uni-next-page"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        )}
      </Card>

      <UniverseCompanyDialog open={dialog !== null} onOpenChange={(v) => !v && setDialog(null)} initial={dialog} onSaved={refresh} />
      <AlertDialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <AlertDialogContent data-testid="uni-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirmDel?.company} from the Universe?</AlertDialogTitle>
            <AlertDialogDescription>Only companies not yet in the CRM can be removed. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="uni-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white gap-2" data-testid="uni-delete-confirm"
              onClick={async (e) => {
                e.preventDefault();
                setDeleting(true);
                try { await delRow(confirmDel); setConfirmDel(null); } catch {} finally { setDeleting(false); }
              }}>
              {deleting && <Loader2 className="h-4 w-4 animate-spin" />} Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <RelatedDialog row={related} onOpenChange={setRelated} />
      <UniverseAccountsDialog row={accounts} onOpenChange={setAccounts} />
    </div>
  );
}
